import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "antd/es/select";
import cn from "classnames";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";

import SelectField from "@app/component/input/SelectField";
import useView from "@app/component/view/utils/useView";
import { getFunnels } from "@app/store/deal";
import { getFavoriteFunnels } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import sortFunnelList from "@app/util/sortFunnelList";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./QuickFunnelFilter.module.scss";

interface Props {
  favoriteFunnelsIds: Array<Funnel["id"]>;
  funnels: Funnel[];
}

const QuickMultiFunnelFilter: React.FC<Props> = ({ favoriteFunnelsIds, funnels }) => {
  const analyticIssuer = useAnalytics();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Deal>();

  const handleChange = useCallback(
    (funnelIds: Array<Funnel["id"]>) => {
      updateFilterModel({
        funnel: funnelIds.length
          ? { operator: FilterOperator.IN_ANY, value: funnelIds }
          : undefined,
      });
      analyticIssuer?.clicked(["Select Funnel"]);
    },
    [analyticIssuer, updateFilterModel]
  );

  const funnelIds: Array<Funnel["id"]> = useMemo(() => {
    if (!filter.funnel || !isSimpleCondition(filter.funnel)) {
      return [];
    }
    return filter.funnel.operator === FilterOperator.IN_ANY
      ? (filter.funnel.value as Array<Funnel["id"]>)
      : filter.funnel.operator === FilterOperator.EQUALS
      ? [filter.funnel.value as Funnel["id"]]
      : [];
  }, [filter]);

  const sortedFunnels = useMemo(
    () => sortFunnelList(funnels, favoriteFunnelsIds),
    [favoriteFunnelsIds, funnels]
  );

  return (
    <SelectField<Array<Funnel["id"]>>
      allowClear
      className={cn(styles.container, styles.multiselect)}
      dropdownMatchSelectWidth={false}
      maxTagCount="responsive"
      mode="multiple"
      onChange={handleChange}
      placeholder={funnelIds.length ? undefined : "All Funnels"}
      value={funnelIds}
    >
      {sortedFunnels.map((funnel) => (
        <Select.Option className={styles.option} key={funnel.id} value={funnel.id}>
          <p className={styles.optionTitle}>{funnel.name}</p>
          {favoriteFunnelsIds.includes(funnel.id) && (
            <FontAwesomeIcon className={styles.starIcon} icon={faStar} />
          )}
        </Select.Option>
      ))}
    </SelectField>
  );
};

const mapStateToProps = (state: RootState) => ({
  favoriteFunnelsIds: getFavoriteFunnels(state),
  funnels: getFunnels(state),
});

export default connect(mapStateToProps)(QuickMultiFunnelFilter);
